const S = ({ className = '' }) => (
  <div className={`animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800 ${className}`} />
)

export const DashboardSkeleton = () => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <S className="h-7 w-40" />
        <S className="h-4 w-56" />
      </div>
      <S className="h-4 w-32 hidden sm:block" />
    </div>

    {/* stat cards */}
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <S className="h-4 w-24" />
            <S className="h-9 w-9 rounded-xl" />
          </div>
          <S className="h-8 w-16" />
          <S className="h-3 w-20" />
        </div>
      ))}
    </div>

    {/* two col section */}
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 space-y-4">
        <S className="h-5 w-32" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <S className="h-9 w-9 rounded-xl shrink-0" />
            <div className="flex-1 space-y-1.5">
              <S className="h-4 w-3/4" />
              <S className="h-3 w-1/2" />
            </div>
            <S className="h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 space-y-3">
        <S className="h-5 w-28" />
        {[...Array(4)].map((_, i) => (
          <S key={i} className="h-12 w-full rounded-xl" />
        ))}
      </div>
    </div>
  </div>
)

export const TableSkeleton = ({ rows = 8, cols = 5 }) => (
  <div className="space-y-5">
    {/* header */}
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <S className="h-7 w-36" />
        <S className="h-4 w-48" />
      </div>
      <S className="h-9 w-28 rounded-xl" />
    </div>

    {/* filters */}
    <div className="flex gap-3 flex-wrap">
      <S className="h-9 w-64 rounded-xl" />
      {[...Array(3)].map((_, i) => <S key={i} className="h-9 w-28 rounded-xl" />)}
    </div>

    {/* table */}
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* header row */}
      <div className="flex gap-4 px-5 py-3 border-b border-gray-100 dark:border-gray-800">
        {[...Array(cols)].map((_, i) => <S key={i} className="h-3.5 flex-1 rounded-md" />)}
      </div>
      {/* data rows */}
      {[...Array(rows)].map((_, r) => (
        <div key={r} className={`flex gap-4 px-5 py-4 items-center ${r !== rows - 1 ? 'border-b border-gray-50 dark:border-gray-800/60' : ''}`}>
          {[...Array(cols)].map((_, c) => (
            <S key={c} className={`flex-1 rounded-md ${c === 0 ? 'h-5' : 'h-3.5'}`} style={{ maxWidth: c === cols - 1 ? '80px' : undefined }} />
          ))}
        </div>
      ))}
    </div>
  </div>
)

export const SettingsSkeleton = () => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <S className="h-7 w-44" />
        <S className="h-4 w-64" />
      </div>
      <S className="h-10 w-32 rounded-xl" />
    </div>
    <div className="grid lg:grid-cols-5 gap-5">
      {/* nav */}
      <div className="lg:col-span-1 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 space-y-1">
        {[...Array(5)].map((_, i) => <S key={i} className="h-10 w-full rounded-xl" />)}
      </div>
      {/* content */}
      <div className="lg:col-span-4 space-y-5">
        {[...Array(3)].map((_, b) => (
          <div key={b} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <S className="h-7 w-7 rounded-lg" />
              <S className="h-4 w-28" />
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <S className="h-3 w-20" />
                  <S className="h-10 w-full rounded-xl" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)

export const CardGridSkeleton = ({ count = 6 }) => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <S className="h-7 w-36" />
        <S className="h-4 w-48" />
      </div>
      <S className="h-9 w-28 rounded-xl" />
    </div>
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <S className="h-5 w-32" />
            <S className="h-6 w-16 rounded-full" />
          </div>
          <S className="h-3.5 w-full" />
          <S className="h-3.5 w-4/5" />
          <div className="flex gap-2 pt-1">
            <S className="h-7 w-20 rounded-lg" />
            <S className="h-7 w-20 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  </div>
)

export default S
